import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, User, Mail, Lock, Package, ChevronRight, ChevronLeft, Check, Sparkles, Shield, BarChart3, Bell, Users, FileText, Wrench, LayoutDashboard, FolderKanban, CalendarClock } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import api from '@/lib/api';
import { toast } from 'sonner';

const STEPS = ['Organization', 'Admin Account', 'Review'];

const moduleIcons = {
  portfolio_management: LayoutDashboard,
  project_overview: FolderKanban,
  project_scheduling: CalendarClock,
  resource_management: Users,
  hse_management: Shield,
  quality_management: Check,
  contracts_claims: FileText,
  procurement: Wrench
};

export default function OrganizationSetup() {
  const navigate = useNavigate();
  const { setupOrganization } = useAuth();
  const { isDark } = useTheme();
  const [step, setStep] = useState(0);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    industry: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    confirm_password: '',
    selected_modules: []
  });
  
  const [errors, setErrors] = useState({});

  useEffect(() => {
    // No need to fetch modules - all modules are always enabled
  }, []);

  const validateStep = () => {
    const newErrors = {};
    
    if (step === 0) {
      if (!formData.name.trim()) newErrors.name = 'Organization name is required';
      if (!formData.industry.trim()) newErrors.industry = 'Industry is required';
    }
    
    if (step === 1) {
      if (!formData.admin_name.trim()) newErrors.admin_name = 'Name is required';
      if (!formData.admin_email.trim()) newErrors.admin_email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.admin_email)) newErrors.admin_email = 'Invalid email format';
      if (!formData.admin_password) newErrors.admin_password = 'Password is required';
      else if (formData.admin_password.length < 6) newErrors.admin_password = 'Password must be at least 6 characters';
      if (formData.admin_password !== formData.confirm_password) newErrors.confirm_password = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(s => Math.min(s + 1, STEPS.length - 1));
    }
  };

  const prevStep = () => setStep(s => Math.max(s - 1, 0));

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      await setupOrganization({
        name: formData.name,
        industry: formData.industry,
        admin_name: formData.admin_name,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
      });
      toast.success('Organization created successfully!');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 0:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <Building2 className="w-12 h-12 text-[#1E4E8C] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white">Organization Details</h2>
              <p className="text-gray-400 mt-2">Tell us about your organization</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Organization Name</Label>
                <Input
                  data-testid="org-name-input"
                  value={formData.name}
                  onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                  placeholder="Acme Construction Inc."
                  className="mt-1 bg-[#1F2937] border-[#3A3F4A] text-white"
                />
                {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
              </div>
              
              <div>
                <Label className="text-gray-300">Industry</Label>
                <select
                  data-testid="org-industry-select"
                  value={formData.industry}
                  onChange={e => setFormData(p => ({ ...p, industry: e.target.value }))}
                  className="w-full mt-1 px-3 py-2 bg-[#1F2937] border border-[#3A3F4A] rounded-md text-white focus:outline-none focus:ring-2 focus:ring-[#1E4E8C]"
                >
                  <option value="">Select industry...</option>
                  <option value="Construction">Construction</option>
                  <option value="Oil & Gas">Oil & Gas</option>
                  <option value="Mining">Mining</option>
                  <option value="Infrastructure">Infrastructure</option>
                  <option value="Power & Energy">Power & Energy</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Other">Other</option>
                </select>
                {errors.industry && <p className="text-red-400 text-sm mt-1">{errors.industry}</p>}
              </div>
            </div>
          </motion.div>
        );
        
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <User className="w-12 h-12 text-[#143A6B] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white">Admin Account</h2>
              <p className="text-gray-400 mt-2">Create your administrator account</p>
            </div>
            
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300">Full Name</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="admin-name-input"
                    value={formData.admin_name}
                    onChange={e => setFormData(p => ({ ...p, admin_name: e.target.value }))}
                    placeholder="John Smith"
                    className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white"
                  />
                </div>
                {errors.admin_name && <p className="text-red-400 text-sm mt-1">{errors.admin_name}</p>}
              </div>
              
              <div>
                <Label className="text-gray-300">Email</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="admin-email-input"
                    type="email"
                    value={formData.admin_email}
                    onChange={e => setFormData(p => ({ ...p, admin_email: e.target.value }))}
                    placeholder="john@acme.com"
                    className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white"
                  />
                </div>
                {errors.admin_email && <p className="text-red-400 text-sm mt-1">{errors.admin_email}</p>}
              </div>
              
              <div>
                <Label className="text-gray-300">Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="admin-password-input"
                    type="password"
                    value={formData.admin_password}
                    onChange={e => setFormData(p => ({ ...p, admin_password: e.target.value }))}
                    placeholder="••••••••"
                    className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white"
                  />
                </div>
                {errors.admin_password && <p className="text-red-400 text-sm mt-1">{errors.admin_password}</p>}
              </div>
              
              <div>
                <Label className="text-gray-300">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input
                    data-testid="admin-confirm-password-input"
                    type="password"
                    value={formData.confirm_password}
                    onChange={e => setFormData(p => ({ ...p, confirm_password: e.target.value }))}
                    placeholder="••••••••"
                    className="pl-10 bg-[#1F2937] border-[#3A3F4A] text-white"
                  />
                </div>
                {errors.confirm_password && <p className="text-red-400 text-sm mt-1">{errors.confirm_password}</p>}
              </div>
            </div>
          </motion.div>
        );
        
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-8">
              <Check className="w-12 h-12 text-[#0E7C6B] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white">Review & Confirm</h2>
              <p className="text-gray-400 mt-2">Review your setup before creating</p>
            </div>
            
            <div className="space-y-4">
              <div className="bg-[#1F2937] border border-[#3A3F4A] rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-2">Organization</h3>
                <p className="text-white font-semibold">{formData.name}</p>
                <p className="text-gray-400 text-sm">{formData.industry}</p>
              </div>
              
              <div className="bg-[#1F2937] border border-[#3A3F4A] rounded-lg p-4">
                <h3 className="text-sm font-medium text-gray-400 mb-2">Administrator</h3>
                <p className="text-white font-semibold">{formData.admin_name}</p>
                <p className="text-gray-400 text-sm">{formData.admin_email}</p>
              </div>
              
              <div className="bg-gradient-to-r from-[#1E4E8C]/20 to-[#143A6B]/20 border border-[#1E4E8C]/30 rounded-lg p-4">
                <h3 className="text-white font-semibold">All Modules Included</h3>
                <p className="text-gray-400 text-sm mt-1">Your organization will have access to all platform modules</p>
              </div>
            </div>
          </motion.div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A] flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={isDark ? "/panorix-sidebar-dark.svg" : "/panorix-sidebar-light.svg"} alt="Panorix" className="w-48 h-auto mx-auto" />
        </div>
        
        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8 gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                i === step ? 'bg-[#1E4E8C] text-white' :
                i < step ? 'bg-[#0E7C6B] text-white' :
                'bg-[#3A3F4A] text-gray-500'
              }`}>
                {i < step ? <Check className="w-4 h-4" /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`w-8 h-0.5 mx-1 ${i < step ? 'bg-[#0E7C6B]' : 'bg-[#3A3F4A]'}`} />
              )}
            </div>
          ))}
        </div>
        
        {/* Step Name */}
        <p className="text-center text-gray-400 text-sm mb-6">Step {step + 1}: {STEPS[step]}</p>
        
        {/* Card */}
        <div className="bg-[#0f1629] border border-[#3A3F4A] rounded-xl p-6 shadow-xl">
          <AnimatePresence mode="wait">
            {renderStepContent()}
          </AnimatePresence>
          
          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-[#3A3F4A]">
            {step > 0 ? (
              <Button
                data-testid="prev-step-btn"
                variant="ghost"
                onClick={prevStep}
                className="text-gray-400 hover:text-white"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            ) : (
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-400 hover:text-white"
              >
                Cancel
              </Button>
            )}
            
            {step < STEPS.length - 1 ? (
              <Button
                data-testid="next-step-btn"
                onClick={nextStep}
                className="bg-[#1E4E8C] hover:bg-[#1E4E8C]/90 text-white"
              >
                Continue
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                data-testid="create-org-btn"
                onClick={handleSubmit}
                disabled={loading}
                className="bg-gradient-to-r from-[#1E4E8C] to-[#143A6B] hover:opacity-90 text-white"
              >
                {loading ? 'Creating...' : 'Create Organization'}
              </Button>
            )}
          </div>
        </div>
        
        {/* Footer */}
        <p className="text-center text-gray-500 text-sm mt-6">
          Already have an account?{' '}
          <button onClick={() => navigate('/')} className="text-[#1E4E8C] hover:underline">
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
}
